/**
 * Audio feature module — recording lifecycle + CDN audio-clip ready/failed
 * verbs. Case bodies moved verbatim from embeddedBridge.ts.
 *
 * recordingComplete also writes the delivered clip into audioStore
 * (pendingRecordedClip) in addition to its original CustomEvent dispatch —
 * see audioStore.ts and RecordPanel.tsx for the mount-time pickup that
 * closes the loss window when RecordPanel is unmounted when this arrives.
 */

import type { HandlerMap } from '../dispatch';
import { enqueueSendToNative } from '../transport';
import { capabilities } from '@/capabilities.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { getBlob, stashBlob } from '@/previs/audioBlobStore.ts';

export interface RecordingStartedMessage {
  type: 'recordingStarted';
  payload: Record<string, never>;
}

export interface RecordingCompleteMessage {
  type: 'recordingComplete';
  payload: {
    clipId: number;
    duration: number;
    peaks: number[];
    label: string;
    lane: number;
  };
}

export interface RecordingFailedMessage {
  type: 'recordingFailed';
  payload: { error: string };
}

export interface AudioClipReadyMessage {
  type: 'audioClipReady';
  payload: { clipId: number };
}

export interface AudioClipFailedMessage {
  type: 'audioClipFailed';
  payload: { clipId: number; reason: string };
}

export interface SetAudioClipBytesMessage {
  type: 'setAudioClipBytes';
  /** `fileUrl` is bare JSON null on every absent/failure path (no row, no blob, no open DB, no
   *  cache directory, or a throw) — never the string "null", never "". */
  payload: { clipId: number; requestId: string; fileUrl: string | null };
}

export type AudioMessage =
  | RecordingStartedMessage
  | RecordingCompleteMessage
  | RecordingFailedMessage
  | AudioClipReadyMessage
  | AudioClipFailedMessage
  | SetAudioClipBytesMessage;

// ─── On-demand clip bytes (requestAudioClipBytes / setAudioClipBytes) ─────
//
// WHY THIS EXISTS. A clip recorded through Unity's own mic never sends its bytes to the web app:
// AudioRecordingManager writes the WAV straight into the open .previs and `recordingComplete` carries
// only metadata + peaks, so audioBlobStore has no entry for it — deliberately, since embedded
// playback is Unity's. Every byte consumer therefore falls back to reading SQLite at
// `_embeddedSqlitePath`, and on the board route that path is board_slim.sqlite: a copy built ONCE at
// project open. Record, then export without leaving the board, and the fallback reads a database that
// predates the recording — the clip exports with rawBase64 null and the HTML plays silence while
// still drawing the clip bar. Re-opening the project (or any RepushProjectToLiveWebView) rebuilds the
// slim, which is why the bug looked intermittent.
//
// Unity answers this verb from DocumentManager.GetCurrentOpenDocumentDatabase() — the same handle
// AudioRecordingManager writes to — so it can never be stale.
//
// TRANSPORT. The reply carries a file:// URL, not bytes, exactly like requestShotFullImage: Unity
// extracts the blob byte-for-byte into the project cache directory and we fetch it. Base64 through
// EvaluateJavaScript would mean multi-megabyte JS strings (a WAV minute is ~5 MB before the 1.37×),
// which is the cost fullImage.ts exists to avoid; the bytes have to become a Uint8Array here either
// way, and fetch(file://) is proven in this WebView.
//
// Like fullImage, Unity NEVER replies to a request it cannot parse (clipId <= 0, bad JSON), so the
// timeout below is the only thing that settles such a promise. Resolving null rather than rejecting
// is deliberate: every caller's fallback is "carry on without these bytes".

/** Long enough for a large WAV extraction + file read on a loaded device, short enough that an
 *  export never stalls behind a request Unity was never going to answer. */
export const AUDIO_CLIP_BYTES_TIMEOUT_MS = 15_000;

/** clipId → the ONE request in flight, so two consumers asking at once share a round trip. */
const pendingClipBytes = new Map<number, {
  requestId: string;
  resolvers: Array<(bytes: Uint8Array | null) => void>;
  timer: ReturnType<typeof setTimeout>;
}>();
let clipBytesSeq = 0;

/** Settle the live request for a clip, if `requestId` still names it. One filter covers a reply
 *  nobody asked for, a duplicate, and the answer to a request the timeout already retired. EVERY
 *  exit runs through here — including the timeout — so a second asker that joined an in-flight
 *  request is never left holding a promise that will not settle. */
function settleClipBytes(clipId: number, requestId: string, bytes: Uint8Array | null): void {
  const entry = pendingClipBytes.get(clipId);
  if (!entry || entry.requestId !== requestId) return;
  clearTimeout(entry.timer);
  pendingClipBytes.delete(clipId);
  for (const resolve of entry.resolvers) resolve(bytes);
}

/**
 * Fetch one clip's raw bytes from Unity's live document and stash them, or resolve null.
 *
 * Resolves the ALREADY-STASHED bytes without a round trip when the store has them — the point of the
 * verb is the sparse case, and a redundant ask costs Unity a full blob extraction to disk.
 */
export function requestAudioClipBytes(clipId: number): Promise<Uint8Array | null> {
  const held = getBlob(clipId);
  if (held) return Promise.resolve(held);
  // enqueueSendToNative silently no-ops off-bridge, which would leave the promise to time out.
  if (!capabilities.bridge || !Number.isFinite(clipId) || clipId <= 0) return Promise.resolve(null);

  const existing = pendingClipBytes.get(clipId);
  if (existing) return new Promise(resolve => { existing.resolvers.push(resolve); });

  const requestId = `audioClipBytes-${++clipBytesSeq}-${clipId}`;
  return new Promise<Uint8Array | null>(resolve => {
    const timer = setTimeout(() => {
      console.warn(`[bridge] requestAudioClipBytes timed out for clip ${clipId}`);
      settleClipBytes(clipId, requestId, null);
    }, AUDIO_CLIP_BYTES_TIMEOUT_MS);
    pendingClipBytes.set(clipId, { requestId, resolvers: [resolve], timer });
    enqueueSendToNative('requestAudioClipBytes', { clipId, requestId });
  });
}

/**
 * Heal the blob store for a set of clips before something reads it wholesale — the export gather, the
 * share pre-upload. Sequential rather than parallel: each answer costs Unity a full blob extraction to
 * disk, and a 40-clip project firing them all at once is a disk-write spike on a device with this
 * project's jetsam history. Ids already backed by a blob cost nothing (see requestAudioClipBytes).
 */
export async function ensureAudioClipBytes(clipIds: number[]): Promise<void> {
  if (!capabilities.bridge) return;
  for (const id of clipIds) {
    if (getBlob(id)) continue;
    await requestAudioClipBytes(id);
  }
}

export const audioHandlers = {
  recordingStarted: (_msg: RecordingStartedMessage) => {
    useUiStore.getState().setIsRecording(true);
  },
  recordingComplete: (msg: RecordingCompleteMessage) => {
    const { clipId, duration, peaks, label, lane } = msg.payload;
    useUiStore.getState().setIsRecording(false);

    // Bump _nextId so future web app allocations don't collide with Unity's
    const store = useProjectStore.getState();
    if (clipId >= store._nextId) {
      useProjectStore.setState({ _nextId: clipId + 1 });
    }

    // Store fallback (latent bug 2 fix): if RecordPanel is unmounted when the
    // event below fires, the recorded clip was previously lost. Stash it so a
    // later mount can stage it. Must run BEFORE the dispatch below — the
    // dispatch is synchronous, so a mounted listener's clear would otherwise
    // run first and this write would leave a stale pending clip behind.
    useAudioStore.getState().setPendingRecordedClip({ clipId, duration, peaks: new Float32Array(peaks), label, lane });

    // Dispatch event for RecordPanel/AudioWorkspace to handle
    window.dispatchEvent(new CustomEvent('__recordingComplete', {
      detail: { clipId, duration, peaks: new Float32Array(peaks), label, lane },
    }));
  },
  recordingFailed: (msg: RecordingFailedMessage) => {
    useUiStore.getState().setIsRecording(false);
    window.dispatchEvent(new CustomEvent('__recordingFailed', {
      detail: { error: msg.payload.error },
    }));
  },
  audioClipReady: (msg: AudioClipReadyMessage) => {
    // Unity has finished downloading + writing a CDN audio clip to SQLite.
    // Dispatch event so components (e.g., ScratchPanel) can dismiss loading overlays.
    window.dispatchEvent(new CustomEvent('__audioClipReady', {
      detail: { clipId: msg.payload.clipId },
    }));
  },
  audioClipFailed: (msg: AudioClipFailedMessage) => {
    // Unity failed to download or decode an audio clip.
    // Dispatch event so components can show an error instead of silently timing out.
    window.dispatchEvent(new CustomEvent('__audioClipFailed', {
      detail: { clipId: msg.payload.clipId, reason: msg.payload.reason },
    }));
  },
  setAudioClipBytes: (msg: SetAudioClipBytesMessage) => {
    const { clipId, requestId, fileUrl } = msg.payload;
    if (fileUrl === null || typeof fileUrl !== 'string' || fileUrl === '') {
      settleClipBytes(clipId, requestId, null);
      return;
    }
    // The stash happens HERE, not at the call site, so a second consumer that never asked still
    // benefits — that is the whole "every downstream consumer heals" property.
    void (async () => {
      try {
        const resp = await fetch(fileUrl);
        const bytes = new Uint8Array(await resp.arrayBuffer());
        if (bytes.length === 0) { settleClipBytes(clipId, requestId, null); return; }
        stashBlob(clipId, bytes);
        settleClipBytes(clipId, requestId, bytes);
      } catch (e) {
        console.warn('[bridge] setAudioClipBytes: failed to read', fileUrl, e);
        settleClipBytes(clipId, requestId, null);
      }
    })();
  },
} satisfies HandlerMap;

/** Test-only reset — module state is process-wide, so suites would leak into each other. */
export function __resetAudioClipBytesForTests(): void {
  for (const entry of pendingClipBytes.values()) clearTimeout(entry.timer);
  pendingClipBytes.clear();
  clipBytesSeq = 0;
}
